// src/pages/ConversationToolkitPage.jsx
import React, { useState, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  BarChart3,
  HelpCircle,
  Shield,
  Mic,
  Layers,
  FolderOpen,
  Brain,
  Users,
  Sparkles,
  ChevronLeft,
  Clock,
  Award,
  BookOpen,
  ArrowRight,
  Target,
  Heart,
  Zap
} from 'lucide-react';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import { useApp } from '../context/AppContext';

// Lazy load all storytelling tools for performance
const OscillatingNarrativeBuilder = lazy(() => import('../components/tools/OscillatingNarrativeBuilder'));
const EmotionalArcMapper = lazy(() => import('../components/tools/EmotionalArcMapper'));
const DoYouKnowFamilyScale = lazy(() => import('../components/tools/DoYouKnowFamilyScale'));
const VulnerabilityCalibrator = lazy(() => import('../components/tools/VulnerabilityCalibrator'));
const StoryRehearsalRecorder = lazy(() => import('../components/tools/StoryRehearsalRecorder'));
const StoryStructureBuilder = lazy(() => import('../components/tools/StoryStructureBuilder'));
const StoryInventoryBuilder = lazy(() => import('../components/tools/StoryInventoryBuilder'));
const StoryImpactAnalyser = lazy(() => import('../components/tools/StoryImpactAnalyser'));
const StoryListeningPracticeGuide = lazy(() => import('../components/tools/StoryListeningPracticeGuide'));

const ConversationToolkitPage = () => {
  const [activeTool, setActiveTool] = useState(null);
  const { toolsUsage = {} } = useApp();

  const tools = [
    {
      id: 'story-inventory-builder',
      title: 'Story Inventory Builder',
      subtitle: 'Map Your Narrative Landscape',
      description: 'Discover and catalogue the stories you already tell. Identify patterns across your story landscape and recognise which narratives shape your identity.',
      icon: FolderOpen,
      heroImage: '/images/mod-01-inventory.png',
      color: 'from-indigo-500 to-purple-500',
      category: 'Module 1',
      estimatedTime: '20 mins',
      component: StoryInventoryBuilder,
      module: 1,
      priority: 5,
      features: [
        '6 life domains with guided prompts',
        'Story type categorisation',
        'Emotional tone tagging',
        'Pattern analysis dashboard'
      ],
      whenToUse: 'Start here to map the stories you tell across different areas of your life'
    },
    {
      id: 'story-impact-analyser',
      title: 'Story Impact Analyser',
      subtitle: 'A Research-Informed Story Checklist',
      description: 'Check your story against four qualities drawn from storytelling research (specific detail, sensory immersion, memorable hooks and shared experience) with a quick text checklist. It counts features in your writing; it can\'t measure a listener\'s brain. Get specific improvement suggestions.',
      icon: Brain,
      heroImage: '/images/mod-02-impact.png',
      color: 'from-purple-500 to-indigo-500',
      category: 'Module 2',
      estimatedTime: '15 mins',
      component: StoryImpactAnalyser,
      module: 2,
      priority: 6,
      features: [
        'Specificity and relatability check',
        'Sensory and stakes check',
        'Memorable-detail check',
        'Before/after comparison'
      ],
      whenToUse: 'When you want to understand why some stories stick whilst others fade'
    },
    {
      id: 'oscillating-narrative-builder',
      title: 'Oscillating Narrative Builder',
      subtitle: 'Turn Struggle Stories into Fuller, More Honest Ones',
      description: 'Restructure descending narratives ("everything went wrong") into oscillating narratives ("it was hard AND I grew"). Marshall Duke calls this oscillating pattern the most healthful kind of family story.',
      icon: TrendingUp,
      heroImage: '/images/mod-03-oscillating.png',
      color: 'from-purple-500 to-pink-500',
      category: 'Module 3',
      estimatedTime: '15 mins',
      component: OscillatingNarrativeBuilder,
      module: 3,
      priority: 1,
      features: [
        'Builds on Marshall Duke\'s family narrative work',
        'Step-by-step transformation guide',
        'Side-by-side comparison view',
        'Truth check validation'
      ],
      whenToUse: 'When you need to reframe a struggle story without toxic positivity'
    },
    {
      id: 'story-structure-builder',
      title: 'Story Structure Builder',
      subtitle: 'Master the 5-Part Story Anatomy',
      description: 'Learn our five-part story structure (Context, Catalyst, Complication, Climax, Consequence), a craft model rather than a universal law, and adapt it for different contexts whilst staying authentic.',
      icon: Layers,
      heroImage: '/images/mod-04-structure.png',
      color: 'from-amber-500 to-orange-500',
      category: 'Module 4',
      estimatedTime: '20 mins',
      component: StoryStructureBuilder,
      module: 4,
      priority: 4,
      features: [
        '5-part story structure',
        '4 context types (Professional, Social, Intimate, Public)',
        'Context-specific guidance',
        'Save multiple versions'
      ],
      whenToUse: 'When you need to adapt the same story for different audiences'
    },
    {
      id: 'emotional-arc-mapper',
      title: 'Emotional Arc Mapper',
      subtitle: 'Visualise Your Story\'s Journey',
      description: 'Map the emotional journey of your story. Identify where sensory details are missing, highlight abstract vs. evocative language, and improve pacing.',
      icon: BarChart3,
      heroImage: '/images/mod-05-arc.png',
      color: 'from-purple-500 to-pink-500',
      category: 'Module 5',
      estimatedTime: '15 mins',
      component: EmotionalArcMapper,
      module: 5,
      priority: 2,
      features: [
        'Visual emotional arc chart',
        'Sensory detail detection',
        'Abstract language highlighter',
        'Section-by-section feedback'
      ],
      whenToUse: 'When your story feels flat or lacks emotional variation'
    },
    {
      id: 'vulnerability-calibrator',
      title: 'Vulnerability Calibrator',
      subtitle: 'Our 10% Rule of Thumb for Disclosure',
      description: 'Assess relationship depth and apply our 10% Rule of thumb for vulnerability (the course\'s own guide, not a research finding). Avoid sharing too much (oversharing) or too little (shutting down) with context-specific guidance.',
      icon: Shield,
      heroImage: '/images/mod-06-vulnerability.png',
      color: 'from-rose-500 to-pink-500',
      category: 'Module 6',
      estimatedTime: '10 mins',
      component: VulnerabilityCalibrator,
      module: 6,
      priority: 3,
      features: [
        'Relationship intimacy assessment',
        '10% Rule of thumb check',
        'Shame vs. guilt wording check',
        'Context-specific warnings'
      ],
      whenToUse: 'Before sharing vulnerable stories to calibrate appropriate depth'
    },
    {
      id: 'story-rehearsal-recorder',
      title: 'Story Rehearsal Recorder',
      subtitle: 'Practise Delivery & Compare Takes',
      description: 'Record audio or video of your story delivery. Self-assess filler words, pacing, pauses, and confidence. Compare your takes within a session, and download any you want to keep.',
      icon: Mic,
      heroImage: '/images/mod-07-delivery.png',
      color: 'from-blue-500 to-cyan-500',
      category: 'Module 7',
      estimatedTime: '10 mins',
      component: StoryRehearsalRecorder,
      module: 7,
      priority: 4,
      features: [
        'Audio/video recording',
        'Guided self-assessment',
        'Progress tracking',
        'Before/after comparison'
      ],
      whenToUse: 'When preparing to tell an important story and want to refine delivery'
    },
    {
      id: 'story-listening-practice',
      title: 'Story Listening Practice Guide',
      subtitle: 'Master Empathic Receiving',
      description: 'Practise empathic listening through scenario-based exercises. Learn to create space for others\' stories without redirecting, minimising, or fixing.',
      icon: Users,
      heroImage: '/images/mod-08-story-listening.png',
      color: 'from-green-500 to-emerald-500',
      category: 'Module 8',
      estimatedTime: '15 mins',
      component: StoryListeningPracticeGuide,
      module: 8,
      priority: 7,
      features: [
        '5 listening scenarios',
        'Immediate feedback',
        'Pitfall identification',
        'Principles from Rogers, White and Epston, and Bohm'
      ],
      whenToUse: 'When you want to improve your ability to hold space for others\' stories'
    },
    {
      id: 'do-you-know-family-scale',
      title: '"Do You Know?" Family Narrative Scale',
      subtitle: 'Explore Your Family Story Knowledge',
      description: 'Marshall Duke & Robyn Fivush\'s research scale (Duke, Lazarus & Fivush, 2008). Assess your intergenerational narrative knowledge and identify gaps in family story awareness.',
      icon: HelpCircle,
      heroImage: '/images/mod-03-oscillating.png',
      color: 'from-violet-500 to-purple-500',
      category: 'Supplementary',
      estimatedTime: '10 mins',
      component: DoYouKnowFamilyScale,
      module: 3,
      priority: 8,
      features: [
        'All 20 original scale questions',
        'Comprehensive research attribution',
        'Rough score guide (our own bands)',
        'Reflection prompts'
      ],
      whenToUse: 'To understand your family narrative baseline and discover story gaps'
    }
  ];

  // Sort tools by module order
  const sortedTools = [...tools].sort((a, b) => {
    if (a.module === b.module) return a.priority - b.priority;
    return a.module - b.module;
  });

  const ToolCard = ({ tool }) => {
    const stats = toolsUsage[tool.id] || { uses: 0, lastUsed: null };

    return (
      <Card
        hover
        className="p-4 sm:p-6 h-full cursor-pointer"
        onClick={() => setActiveTool(tool.id)}
      >
        <div className="space-y-3 sm:space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden flex-shrink-0 border-2 border-slate-700/50">
              <img
                src={tool.heroImage}
                alt={tool.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const fallback = document.createElement('div');
                  fallback.className = `w-full h-full bg-gradient-to-br ${tool.color} flex items-center justify-center`;
                  const icon = document.createElement('div');
                  icon.className = 'w-8 h-8 text-white';
                  fallback.appendChild(icon);
                  e.target.parentNode.appendChild(fallback);
                }}
              />
            </div>

            <div className="text-right">
              <span className="text-xs text-slate-400">{tool.category}</span>
              {stats.uses > 0 && (
                <div className="text-xs text-green-400 mt-1">
                  Used {stats.uses}x
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white mb-1">{tool.title}</h3>
            <p className="text-xs sm:text-sm text-purple-200 font-medium mb-2 sm:mb-3">{tool.subtitle}</p>
            <p className="text-slate-300 text-xs sm:text-sm mb-3 sm:mb-4">{tool.description}</p>

            {/* When to Use */}
            <div className="p-2 sm:p-3 bg-blue-500/10 rounded-lg mb-3 sm:mb-4">
              <p className="text-xs text-blue-300 font-medium mb-1">When to Use:</p>
              <p className="text-xs text-slate-300">{tool.whenToUse}</p>
            </div>

            {/* Features */}
            <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
              {tool.features.slice(0, 2).map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs text-slate-400">
                  <div className="w-1 h-1 bg-purple-400 rounded-full" />
                  {feature}
                </div>
              ))}
              {tool.features.length > 2 && (
                <div className="text-xs text-slate-400">
                  +{tool.features.length - 2} more features
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-slate-700">
              <span className="flex items-center gap-1 text-xs text-slate-400">
                <Clock className="w-3 h-3" />
                {tool.estimatedTime}
              </span>

              <Button size="sm" variant="ghost" className="text-xs sm:text-sm">
                <span className="hidden sm:inline">Open Tool</span>
                <span className="sm:hidden">Open</span>
                <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  if (activeTool) {
    const tool = tools.find(t => t.id === activeTool);
    const ToolComponent = tool.component;

    return (
      <div className="min-h-screen bg-gradient-to-br from-charcoal to-gunmetal pt-24 md:pt-32">
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="max-w-6xl mx-auto">
            {/* Back Button */}
            <button
              onClick={() => setActiveTool(null)}
              className="flex items-center gap-2 text-purple-300 hover:text-white mb-6 sm:mb-8 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to Toolkit
            </button>

            {/* Tool Component */}
            <Suspense fallback={
              <div className="flex items-center justify-center p-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
                  <p className="text-slate-400">Loading tool...</p>
                </div>
              </div>
            }>
              <ToolComponent />
            </Suspense>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal to-gunmetal pt-24 md:pt-32">
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8 sm:mb-12"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-4 sm:mb-6 inline-block"
            >
              <img
                src="/images/storytelling-toolkit.png"
                alt="Storytelling Toolkit"
                className="w-32 h-32 sm:w-44 sm:h-44 object-contain rounded-2xl mx-auto"
              />
            </motion.div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4 px-4">
              Storytelling Toolkit
            </h1>
            <p className="text-lg sm:text-xl text-slate-200 max-w-2xl mx-auto mb-4 sm:mb-6 px-4">
              Interactive tools to build, analyse, and refine your stories
            </p>
            <p className="text-base sm:text-lg text-slate-300 max-w-3xl mx-auto px-4">
              Each tool draws on published research or established storytelling craft, and is designed to develop specific storytelling skills. Choose based on what you need right now.
            </p>
          </motion.div>

          {/* Quick Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-12"
          >
            <Card className="p-3 sm:p-4 text-center">
              <Award className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 mx-auto mb-1 sm:mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-white">
                {Object.values(toolsUsage).reduce((sum, stat) => sum + (stat.uses || 0), 0)}
              </div>
              <div className="text-xs sm:text-sm text-slate-400">Total Tool Uses</div>
            </Card>

            <Card className="p-3 sm:p-4 text-center">
              <Brain className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400 mx-auto mb-1 sm:mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-white">
                {Object.keys(toolsUsage).length}
              </div>
              <div className="text-xs sm:text-sm text-slate-400">Tools Explored</div>
            </Card>

            <Card className="p-3 sm:p-4 text-center">
              <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-green-400 mx-auto mb-1 sm:mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-white">
                {toolsUsage['oscillating-narrative-builder']?.storiesTransformed || 0}
              </div>
              <div className="text-xs sm:text-sm text-slate-400">Stories Transformed</div>
            </Card>

            <Card className="p-3 sm:p-4 text-center">
              <Heart className="w-6 h-6 sm:w-8 sm:h-8 text-red-400 mx-auto mb-1 sm:mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-white">
                {toolsUsage['story-inventory-builder']?.storiesAdded || 0}
              </div>
              <div className="text-xs sm:text-sm text-slate-400">Stories Catalogued</div>
            </Card>
          </motion.div>

          {/* Tools Grid */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-12"
          >
            {sortedTools.map((tool, idx) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * idx }}
              >
                <ToolCard tool={tool} />
              </motion.div>
            ))}
          </motion.div>

          {/* Usage Tips */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Card className="p-4 sm:p-6 md:p-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 text-center">
                How to Use Your Toolkit
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                <div className="text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Target className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Build Your Foundation</h3>
                  <p className="text-slate-300 text-xs sm:text-sm">
                    Start with the Story Inventory Builder to map your narrative landscape, then use the Impact Analyser to understand what makes stories stick.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Craft & Refine</h3>
                  <p className="text-slate-300 text-xs sm:text-sm">
                    Use the Oscillating Narrative Builder and Emotional Arc Mapper to transform and enhance your stories. Apply the Structure Builder for different contexts.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Practise & Connect</h3>
                  <p className="text-slate-300 text-xs sm:text-sm">
                    Rehearse delivery with the Story Rehearsal Recorder. Calibrate vulnerability appropriately. Master empathic listening with the Listening Practice Guide.
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ConversationToolkitPage;
